import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_preview-suite/artifacts/bkqjgsqq_RteQ_Solutions_Logo-removebg-preview.png";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.suite'), href: '/suite' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.company'), href: '/company' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isActive = (href) => {
    return location.pathname === href;
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen ? 'glass-header' : 'bg-transparent'
        }`}
        data-testid="navigation-header"
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link 
              to="/"
              className="flex-shrink-0"
              data-testid="nav-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="RTEQ Solutions" 
                className="logo-image"
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-[16px] font-medium transition-colors duration-200 relative group ${
                    isActive(link.href) ? 'text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#FF5E00] transition-all duration-300 ${
                    isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-3">
              <a 
                href="https://portal.rteqsolutions.com"
                target="_blank"
                rel="noopener noreferrer"
                className="secondary-button px-5 py-2.5 text-sm"
                data-testid="nav-login-button"
              >
                {t('nav.login')}
              </a>
              <a 
                href="https://portal.rteqsolutions.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="glow-button px-5 py-2.5 text-sm"
                data-testid="nav-get-started-button"
              >
                {t('nav.getStarted')}
              </a>
            </div>

            <div className="hidden lg:block ml-4">
              <LanguageSwitcher />
            </div>

            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`text-2xl font-heading font-bold transition-colors ${
                      isActive(link.href) ? 'text-[#FF5E00]' : 'text-white hover:text-[#FF5E00]'
                    }`}
                    data-testid={`mobile-nav-link-${link.href.replace('/', '') || 'home'}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <div className="flex flex-col items-center space-y-4 pt-8">
                <LanguageSwitcher />
                <a 
                  href="https://portal.rteqsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="secondary-button px-8 py-3 text-base"
                  data-testid="mobile-login-button"
                >
                  {t('nav.login')}
                </a>
                <a 
                  href="https://portal.rteqsolutions.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glow-button px-8 py-3 text-base text-center"
                  data-testid="mobile-get-started-button"
                >
                  {t('nav.getStarted')}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
